import { useEffect, useRef, useState } from "react";
import { useDocumentStore, LIBRARY_BYTES_CAP, type LibraryDocument } from "../store/documentStore";
import { MD } from "./Markdown";

// LibraryView — third top-level view (Chat | Library | Wiki). The
// document library is a persistent per-browser collection of
// text/markdown docs you can later attach to a channel for AI context.
// No editor — read-only preview, upload, rename, remove. Attach
// lives in the channel toolbar, not here.

const ACCEPT_EXTS = [".txt", ".md", ".markdown"];

export function LibraryView() {
  const { documents, addDocument, removeDocument, renameDocument, setTags } = useDocumentStore();
  const [activeId, setActiveId] = useState<string | null>(documents[0]?.id ?? null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [hfBusy, setHfBusy] = useState(false);

  const active = documents.find((d) => d.id === activeId) ?? null;
  const usedBytes = documents.reduce((sum, d) => sum + d.bytes, 0);
  const usedPct = Math.round((usedBytes / LIBRARY_BYTES_CAP) * 100);

  async function handleFiles(fileList: FileList | null) {
    if (!fileList) return;
    setError(null);
    let newestId: string | null = null;
    for (const f of Array.from(fileList)) {
      const lower = f.name.toLowerCase();
      const okExt = ACCEPT_EXTS.some((e) => lower.endsWith(e));
      if (!okExt) {
        setError(`${f.name}: only ${ACCEPT_EXTS.join(", ")} files accepted right now.`);
        continue;
      }
      try {
        const content = await f.text();
        newestId = addDocument(f.name, content);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        break;
      }
    }
    if (newestId) setActiveId(newestId);
  }

  async function handleHfImport() {
    const url = prompt(
      "Paste a HuggingFace model or dataset URL.\n\n" +
      "Examples:\n" +
      "  https://huggingface.co/Qwen/Qwen3-8B\n" +
      "  https://huggingface.co/datasets/HuggingFaceH4/no_robots\n\n" +
      "The model/dataset README.md will be fetched and added to your library.",
    );
    if (!url) return;
    setError(null);
    setHfBusy(true);
    try {
      const rawUrl = toHfRawReadmeUrl(url.trim());
      if (!rawUrl) {
        throw new Error("Not a HuggingFace URL I recognize. Use https://huggingface.co/{owner}/{name} or .../datasets/{owner}/{name}.");
      }
      const resp = await fetch(rawUrl);
      if (!resp.ok) {
        throw new Error(`HuggingFace returned ${resp.status} ${resp.statusText}. Repo may be private or have no README.md.`);
      }
      const content = await resp.text();
      const niceName = hfNameFromUrl(url.trim());
      const id = addDocument(niceName, content);
      setActiveId(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setHfBusy(false);
    }
  }

  function startRename(d: LibraryDocument) {
    setRenaming(d.id);
    setRenameDraft(d.name);
  }
  function commitRename(id: string) {
    renameDocument(id, renameDraft);
    setRenaming(null);
  }
  function cancelRename() {
    setRenaming(null);
    setRenameDraft("");
  }

  return (
    <div className="lib-route">
      <aside className="lib-sidebar">
        <div className="lib-side-head">
          <span className="lib-side-title">documents</span>
          <button
            className="btn btn-ghost lib-add"
            onClick={() => fileInputRef.current?.click()}
            title="Add a .txt or .md file to the library"
          >
            + Add
          </button>
          <button
            className="btn btn-ghost lib-add"
            onClick={() => void handleHfImport()}
            disabled={hfBusy}
            title="Fetch a HuggingFace model or dataset README.md into the library"
          >
            {hfBusy ? "fetching…" : "+ HF"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_EXTS.join(",")}
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              void handleFiles(e.target.files);
              e.target.value = ""; // allow same-file re-upload
            }}
          />
        </div>

        <div className="lib-quota" title={`${usedBytes.toLocaleString()} / ${LIBRARY_BYTES_CAP.toLocaleString()} bytes used`}>
          <div className="lib-quota-bar"><div className="lib-quota-fill" style={{ width: `${Math.min(usedPct, 100)}%` }} /></div>
          <span>{usedPct}% of library cap</span>
        </div>

        {error && <div className="lib-error">{error}</div>}

        {documents.length === 0 ? (
          <div className="lib-empty">
            No documents yet. Click <strong>+ Add</strong> or drop a <code>.txt</code> / <code>.md</code> file anywhere on this panel.
          </div>
        ) : (
          <div className="lib-list">
            {documents.map((d) => (
              <div
                key={d.id}
                className={`lib-item ${d.id === activeId ? "active" : ""}`}
                onClick={() => setActiveId(d.id)}
                onDragOver={(e) => e.preventDefault()}
              >
                {renaming === d.id ? (
                  <input
                    autoFocus
                    className="lib-rename-input"
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onBlur={() => commitRename(d.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRename(d.id);
                      if (e.key === "Escape") cancelRename();
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <div className="lib-item-main">
                    <div className="lib-item-name" onDoubleClick={() => startRename(d)}>
                      {d.name}
                    </div>
                    {d.tags && d.tags.length > 0 && (
                      <div className="lib-item-tags">
                        {d.tags.map((t) => <span key={t} className="lib-tag">{t}</span>)}
                      </div>
                    )}
                  </div>
                )}
                <div className="lib-item-meta">{formatBytes(d.bytes)}</div>
                <button
                  className="lib-item-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Remove "${d.name}" from the library? Channels with it attached keep their copy.`)) {
                      removeDocument(d.id);
                      if (activeId === d.id) setActiveId(null);
                    }
                  }}
                  title="Remove from library"
                  aria-label={`Remove ${d.name}`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </aside>

      <article
        className="lib-article"
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("dragging"); }}
        onDragLeave={(e) => { e.currentTarget.classList.remove("dragging"); }}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.classList.remove("dragging");
          void handleFiles(e.dataTransfer.files);
        }}
      >
        {active ? (
          <div className="lib-preview">
            <div className="lib-preview-head">
              <span className="lib-preview-title">{active.name}</span>
              <span className="lib-preview-meta">{formatBytes(active.bytes)} · added {formatDate(active.added_at)}</span>
            </div>
            <TagsEditor doc={active} onChange={(tags) => setTags(active.id, tags)} />
            {looksLikeMarkdown(active.name)
              ? <div className="lib-preview-body md"><MD>{active.content}</MD></div>
              : <pre className="lib-preview-body lib-preview-plain">{active.content}</pre>}
          </div>
        ) : (
          <div className="lib-empty-pane">
            <h2>Document library</h2>
            <p>Drop a <code>.txt</code> or <code>.md</code> file here, or use <strong>+ Add</strong>.</p>
            <p>Attach docs to a channel from the chat toolbar — the AI sees them as context on every turn.</p>
          </div>
        )}
      </article>
    </div>
  );
}

function looksLikeMarkdown(name: string): boolean {
  const lower = name.toLowerCase();
  return lower.endsWith(".md") || lower.endsWith(".markdown");
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

function TagsEditor({ doc, onChange }: { doc: LibraryDocument; onChange: (tags: string[]) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState((doc.tags ?? []).join(", "));

  // Reset draft when the active doc changes — otherwise the editor
  // shows tags from a previously-selected doc.
  useEffect(() => {
    setDraft((doc.tags ?? []).join(", "));
    setEditing(false);
  }, [doc.id, doc.tags]);

  function commit() {
    const tags = draft.split(",").map((s) => s.trim()).filter(Boolean);
    onChange(tags);
    setEditing(false);
  }

  if (editing) {
    return (
      <div className="lib-tags-edit">
        <input
          autoFocus
          className="lib-tags-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="comma-separated — spec, rfc, q3"
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setDraft((doc.tags ?? []).join(", "));
              setEditing(false);
            }
          }}
        />
        <button className="btn btn-ghost" onClick={commit} style={{ fontSize: 11 }}>Save</button>
      </div>
    );
  }

  const tags = doc.tags ?? [];
  return (
    <div className="lib-tags-row" onClick={() => setEditing(true)} title="Click to edit tags">
      {tags.length > 0
        ? tags.map((t) => <span key={t} className="lib-tag">{t}</span>)
        : <span className="lib-tags-add">+ add tags</span>}
    </div>
  );
}

/** Translate a HuggingFace UI URL into its raw README.md URL.
 *  Models:   huggingface.co/{owner}/{name}
 *  Datasets: huggingface.co/datasets/{owner}/{name}
 *  Spaces:   huggingface.co/spaces/{owner}/{name}
 *  Returns null when the input doesn't look like an HF URL. */
function toHfRawReadmeUrl(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.hostname !== "huggingface.co") return null;
  // Strip trailing slash and leading slash. Path shapes:
  //   /{owner}/{name}            (model)
  //   /datasets/{owner}/{name}   (dataset)
  //   /spaces/{owner}/{name}     (space)
  const parts = parsed.pathname.replace(/^\/+|\/+$/g, "").split("/");
  let prefix = "";
  let owner = "";
  let name = "";
  if (parts.length >= 3 && (parts[0] === "datasets" || parts[0] === "spaces")) {
    prefix = `${parts[0]}/`;
    owner = parts[1];
    name = parts[2];
  } else if (parts.length >= 2) {
    owner = parts[0];
    name = parts[1];
  } else {
    return null;
  }
  if (!owner || !name) return null;
  return `https://huggingface.co/${prefix}${owner}/${name}/raw/main/README.md`;
}

function hfNameFromUrl(url: string): string {
  try {
    const parts = new URL(url).pathname.replace(/^\/+|\/+$/g, "").split("/");
    if (parts.length >= 3 && (parts[0] === "datasets" || parts[0] === "spaces")) {
      return `${parts[0]} · ${parts[1]}/${parts[2]} README.md`;
    }
    if (parts.length >= 2) {
      return `${parts[0]}/${parts[1]} README.md`;
    }
    return "HF README.md";
  } catch {
    return "HF README.md";
  }
}
